import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { KeyRound, RefreshCw, Copy, Eye, EyeOff, AlertTriangle, Plug, ScrollText, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('valoriza_token')}` });

export default function ClockIntegrationAdmin() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [freshKey, setFreshKey] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [audit, setAudit] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        fetch(`${API_URL}/api/admin/integration/api-key`, { headers: auth() }).then(r => r.json()),
        fetch(`${API_URL}/api/admin/integration/audit?limit=30`, { headers: auth() }).then(r => r.json()),
      ]);
      setInfo(a);
      setAudit(b.items || []);
    } catch (e) {
      toast.error('Error cargando datos: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rotate = async () => {
    if (info?.configured && !window.confirm('Al regenerar la API key, la actual dejará de funcionar inmediatamente. La plataforma externa tendrá que actualizar su configuración. ¿Continuar?')) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/integration/api-key/rotate`, {
        method: 'POST', headers: auth(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      setFreshKey(data.key);
      setShowKey(true);
      toast.success('API key generada. ¡Guárdala ahora!');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const copyKey = async () => {
    if (!freshKey) return;
    await navigator.clipboard.writeText(freshKey);
    toast.success('API key copiada al portapapeles');
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Cargando…</div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 flex items-center justify-center flex-shrink-0">
          <Plug className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold font-heading">API de integración — Fichajes</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Endpoint público que la plataforma de gestión de jornada usará para descargar los fichajes de teletrabajo cada noche.
          </p>
        </div>
      </div>

      {/* API key card */}
      <div className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="api-key-card">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-brand" />
          <h2 className="font-semibold">API key activa</h2>
        </div>

        {info?.configured ? (
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Vista parcial:</span>
              <code className="font-mono px-2 py-0.5 rounded bg-muted">{info.preview}</code>
            </div>
            <div className="text-xs text-muted-foreground">
              Creada el {new Date(info.created_at).toLocaleString('es-ES')} por {info.created_by}.
            </div>
          </div>
        ) : (
          <p className="text-sm text-amber-600 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> No hay ninguna API key configurada todavía.
          </p>
        )}

        <Button onClick={rotate} disabled={generating} data-testid="btn-rotate-key" className="gap-2">
          <RefreshCw className={`h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
          {info?.configured ? 'Regenerar API key' : 'Generar API key'}
        </Button>

        {freshKey && (
          <div className="rounded-xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4 space-y-3" data-testid="fresh-key-block">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm font-medium">
              <AlertTriangle className="h-4 w-4" />
              Guarda esta key AHORA mismo. No volverá a mostrarse completa.
            </div>
            <div className="flex items-center gap-2">
              <code className={`flex-1 font-mono text-xs px-3 py-2 rounded bg-white dark:bg-black/30 border border-amber-200 dark:border-amber-800 break-all ${showKey ? '' : 'blur-sm select-none'}`} data-testid="fresh-key-value">
                {freshKey}
              </code>
              <Button size="sm" variant="outline" onClick={() => setShowKey(!showKey)} data-testid="btn-toggle-key">
                {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </Button>
              <Button size="sm" variant="outline" onClick={copyKey} data-testid="btn-copy-key">
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Documentation card */}
      <div className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="doc-card">
        <div className="flex items-center gap-2">
          <ExternalLink className="h-4 w-4 text-brand" />
          <h2 className="font-semibold">Cómo lo consume la plataforma externa</h2>
        </div>
        <pre className="bg-muted/50 rounded-lg p-3 text-xs overflow-x-auto font-mono whitespace-pre">
{`GET ${API_URL}/api/integration/clock?from=2026-01-01&to=2026-01-31&format=sessions
Authorization: Bearer <API_KEY>`}
        </pre>
        <details className="text-sm">
          <summary className="cursor-pointer text-brand hover:underline font-medium">Ver todos los parámetros</summary>
          <ul className="mt-2 space-y-1 text-xs font-mono">
            <li><strong>format</strong> = <code>sessions</code> (entrada+salida pareadas con duración) o <code>events</code> (cada punch suelto)</li>
            <li><strong>from</strong>, <strong>to</strong> = rango (YYYY-MM-DD, máx 366 días)</li>
            <li><strong>email</strong> = email exacto del trabajador (opcional)</li>
            <li><strong>centro_trabajo</strong> = búsqueda parcial en código o descripción (opcional)</li>
            <li><strong>agrupacion</strong> = búsqueda parcial en agrupación (opcional)</li>
            <li><strong>status</strong> = <code>all|complete|incomplete</code> (sólo en sessions)</li>
            <li><strong>limit</strong> = máx 5000 (default 1000)</li>
            <li><strong>skip</strong> = paginación</li>
          </ul>
        </details>
        <p className="text-xs text-muted-foreground">
          Cada registro incluye <strong>email + numero_personal + dni</strong> para que la plataforma destino cruce con sus propios maestros.
        </p>
      </div>

      {/* Audit log */}
      <div className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="audit-card">
        <div className="flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-brand" />
          <h2 className="font-semibold">Auditoría — últimas {audit.length} llamadas</h2>
        </div>
        {audit.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aún no hay llamadas registradas.</p>
        ) : (
          <div className="max-h-80 overflow-y-auto border border-border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 sticky top-0">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold">Fecha</th>
                  <th className="px-3 py-2 font-semibold">IP</th>
                  <th className="px-3 py-2 font-semibold">Formato</th>
                  <th className="px-3 py-2 font-semibold text-right">Devueltos</th>
                  <th className="px-3 py-2 font-semibold">Rango</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((a, i) => (
                  <tr key={i} className="border-t border-border/60">
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{new Date(a.called_at).toLocaleString('es-ES')}</td>
                    <td className="px-3 py-2 font-mono">{a.ip || '—'}</td>
                    <td className="px-3 py-2"><span className="px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-[10px] font-medium">{a.format}</span></td>
                    <td className="px-3 py-2 text-right font-mono">{a.returned} / {a.total_matched}</td>
                    <td className="px-3 py-2 text-[11px] text-muted-foreground">{a.params?.from} → {a.params?.to}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
